// Sammanställning av externa arbetsberedningar (upprättade utanför systemet).
import type { Paragraph, Table } from "docx";
import { downloadWordDocument } from "@/lib/documentGenerator/exportHistory";
import { createStampForCurrentUser } from "@/lib/documentGenerator/watermark";
import { body, buildDocument, h1, table } from "@/lib/documentGenerator/docTheme";
import { documentFooter } from "@/lib/documentGenerator/documentFooter";

export interface ExternalWorkPrepSummaryRow {
  name: string;
  moment?: string;
  week?: string;
  contractor?: string;
  location?: string;
  acknowledged?: string;
  remarks?: string;
}

export const generateExternalWorkPrepSummary = async (
  rows: ExternalWorkPrepSummaryRow[],
  projectName?: string
) => {
  const documentName = "Externa arbetsberedningar – sammanställning";
  const stamp = await createStampForCurrentUser({
    docNumber: "23",
    documentName,
    projectName,
  });

  const children: (Paragraph | Table)[] = [
    h1("1. Externa arbetsberedningar"),
    body(
      "Arbetsberedningar som upprättats av entreprenör eller i annat system. Bas-U har registrerat att de finns, var originalet förvaras och kvitterat granskningen."
    ),
    table(
      ["Beredning / moment", "Vecka", "Entreprenör", "Original förvaras", "Kvitterad"],
      rows.map((r) => [
        [r.name, r.moment].filter(Boolean).join(" – "),
        r.week ?? "",
        r.contractor ?? "",
        r.location ?? "",
        r.acknowledged ?? "Nej",
      ]),
      { columnWidths: [3000, 1200, 2000, 2200, 1200] }
    ),
  ];

  const withRemarks = rows.filter((r) => r.remarks);
  if (withRemarks.length) {
    children.push(h1("2. AI-anmärkningar att följa upp"));
    withRemarks.forEach((r) => {
      children.push(body(`${r.name}: ${r.remarks}`));
    });
  }

  const footer = await documentFooter({ docNumber: "23", documentType: "arbetsberedning" });

  const doc = buildDocument({
    meta: {
      version: footer.version,
      review: footer.review,
      docNumber: "23",
      documentName,
      projectName,
      date: new Date().toISOString(),
    },
    stamp,
    children: [...children, ...footer.children],
  });

  await downloadWordDocument(doc, "23_Externa_arbetsberedningar.docx", {
    documentType: "arbetsberedning",
    docNumber: "23",
    documentName,
    snapshot: rows,
    stamp,
  });
};
